<template>
   <div data-cy="file-upload-v1">
      <LoadingSpinner v-if="isLoading" />
      <template v-else>
         <div
            v-if="previouslyUploadedFileError && !validFileUploaded"
            class="flex flex-row items-center rounded-lg bg-warning p-2 text-xs text-black mb-2"
            data-cy="previous-upload-error"
         >
            <div class="mr-2">
               <IconTriangleSolidExclamation class="mr-auto block h-4 w-4" />
            </div>
            <p>
               {{ $t("system.file_upload.retrival_failure") }}
            </p>
         </div>
         <FileUpload
            v-if="!maximumFileTotalReached"
            :readOnly="maximumFileTotalReached"
            :blockVersionId="block.blockVersionId"
            :acceptedFileTypes="state.acceptedFileTypes"
            @file-uploaded="fileUploaded"
         />
         <div v-if="state?.uploadedFiles?.length > 0" data-cy="uploaded-file-list">
            <li
               v-for="file in state.uploadedFiles"
               :key="file"
               class="flex w-full items-center justify-between rounded-md border border-grey-10 bg-grey-10"
               :class="hasSavedBlockAnswer ? 'px-6 py-4' : 'p-2'"
            >
               <span :class="{ 'pl-4': !hasSavedBlockAnswer }">{{ file }}</span>
               <div class="flex justify-end">
                  <span class="text-grey-70 self-center">{{ $t("system.file_upload.success") }}</span>
                  <LoadingButton
                     v-if="!hasSavedBlockAnswer"
                     data-cy="delete-file"
                     :isLoading="isDeleting"
                     is-icon
                     roundedEdges="md"
                     size="sm"
                     :color="Theme.Black"
                     class="size-12 ml-4"
                     @click="clearPreviouslyUploadedDocuments"
                  >
                     <span class="sr-only">{{ $t("system.file_upload.remove_file") }}</span>
                     <IconTrash class="relative size-4 fill-none" />
                  </LoadingButton>
               </div>
            </li>
         </div>
         <InvalidInputsList :errorList="errorList" :class="{ hidden: !showBlockErrors }" />
      </template>
   </div>
</template>

<script setup lang="ts">
import type { AnswerResp } from "@/data/api-layer/answersApi";
import DocumentsApi from "@/data/api-layer/documentsApi";
import { useI18n } from "@/vue-i18n/vue-i18n";
import type { FlowselBlock } from "@/components/flowsel/flowsel.type";
import { Theme } from "@/types";

export interface FileUploadAnswerData {
   files: string[] | null;
}

export interface FileUploadBlock extends FlowselBlock {
   data: {
      settings: {
         validations: {
            accept: string[];
         };
         maxFilesNumber: number;
      };
   };
}

interface Props {
   block: FileUploadBlock;
   userBlockAnswer: AnswerResp | null;
   showBlockErrors: boolean;
}

// declarations
// -----------------------------------------
const props = defineProps<Props>();
const { t: $t } = useI18n();
const emit = defineEmits(["update-valid", "update-answer"]);

const state = reactive({
   uploadedFiles: props.userBlockAnswer?.data?.files || [],
   acceptedFileTypes: props.block.data?.settings?.validations?.accept,
});
const isLoading = ref<boolean>(true);
const previouslyUploadedFileError = ref<boolean>(false);
const isDeleting = ref<boolean>(false);

// computed
// -----------------------------------------
const hasSavedBlockAnswer = computed(() => {
   return !!props.userBlockAnswer?.data?.files;
});

const maxFilesTotal = computed(() => {
   return props.block.data?.settings?.maxFilesNumber || 1;
});

const maximumFileTotalReached = computed(() => {
   if (!state.uploadedFiles) return false;
   return state.uploadedFiles.length >= maxFilesTotal.value;
});

const validFileUploaded = computed(() => {
   // Errors with uploading are dealt with in the FileUpload component
   // Here we're checking if a file has been uploaded when one is required (multiple files are not yet supported)
   // (Because only one file can be uploaded we don't need to check for uploading errors on 'next' because if a file is uploaded there can't be a current error)
   if (props.block.isRequired && (!state.uploadedFiles || state.uploadedFiles.length < 1)) return false;
   else return true;
});

const errorList = computed(() => {
   if (!validFileUploaded.value)
      return [
         {
            inputId: props.block.id,
            validator: "required",
            message: $t("validation.fileupload.required"),
         },
      ];
   return [];
});

// methods
// -----------------------------------------
function fileUploaded(file) {
   state.uploadedFiles.unshift(file);
   processInputs();
}

function processInputs() {
   if (!validFileUploaded.value) {
      // an input is invalid or has errors
      emit("update-valid", false);
   } else {
      // all inputs are valid. This may be because there is a valid answer, or no answer
      // when this question is not required. For no answer, a different data format is required by the BE.
      const answerToSubmit = state.uploadedFiles?.length > 0 ? { files: state.uploadedFiles } : null;
      emit("update-valid", true);
      emit("update-answer", answerToSubmit);
   }
}

async function getPreviouslyUploadedDocuments() {
   // if a user has previously uploaded a document for this block, but it hasn't yet been saved as a block answer
   // repopulate the block with the documents associated with this block version
   const previousUploadsResponse = await DocumentsApi.getBlockUploads(props.block.blockVersionId);
   return previousUploadsResponse.data;
}

async function clearPreviouslyUploadedDocuments() {
   // if the user has not yet submitted the block, they can delete their uploaded files
   // note that this will clear ALL files for the block
   isDeleting.value = true;
   try {
      await DocumentsApi.deleteBlockUploads(props.block.blockVersionId);
      state.uploadedFiles = [];
   } catch (error) {
      console.error("error clearing previously uploaded documents:", error);
   } finally {
      isDeleting.value = false;
      processInputs();
   }
}

// lifecycle
// -----------------------------------------
onMounted(async () => {
   if (state.uploadedFiles.length) {
      // previous block answer exists
      processInputs();
      isLoading.value = false;
   } else {
      // no submitted answer exists, check if any files exist in s3 for this block version that haven't been submitted
      try {
         const previouslyUploadedFiles = await getPreviouslyUploadedDocuments();
         if (previouslyUploadedFiles.length) {
            previouslyUploadedFiles.forEach((file) => fileUploaded(file.filename));
         }
      } catch (error) {
         console.error("error fetching previously uploaded documents:", error);
         previouslyUploadedFileError.value = true;
      } finally {
         processInputs();
         isLoading.value = false;
      }
   }
});
</script>
